/**
 * Floating chat widget for the public LandingPage.
 *
 * Visitor either types a question or taps the mic to record a voice
 * question (browser MediaRecorder, transcribed server-side via
 * /api/v1/public/chat/transcribe). Replies are text only.
 *
 * Session persistence: a UUID stored in localStorage keeps the same
 * conversation across page reloads.
 */
import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, Mic, Send, X, Loader2, Square } from 'lucide-react';

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  ts: string;
}

interface ChatResponse {
  session_id: string;
  reply: string;
  suggest_email_capture: boolean;
  turn_count: number;
}

const SESSION_KEY = 'vcre_homepage_chat_session_id';
const HISTORY_KEY = 'vcre_homepage_chat_history';
const EMAIL_KEY = 'vcre_homepage_chat_email';

const API_BASE = process.env.REACT_APP_API_URL || '/api/v1';

function getOrCreateSessionId(): string {
  let id = localStorage.getItem(SESSION_KEY);
  if (!id) {
    id = (typeof crypto !== 'undefined' && crypto.randomUUID)
      ? crypto.randomUUID()
      : `s_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem(SESSION_KEY, id);
  }
  return id;
}

function loadHistory(): ChatMessage[] {
  try {
    const raw = localStorage.getItem(HISTORY_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.slice(-30) : [];
  } catch {
    return [];
  }
}

function saveHistory(msgs: ChatMessage[]) {
  try {
    localStorage.setItem(HISTORY_KEY, JSON.stringify(msgs.slice(-30)));
  } catch {
    /* localStorage full or disabled — ignore */
  }
}

const GREETING: ChatMessage = {
  role: 'assistant',
  content:
    "Hey — I'm the VentureOS assistant. Ask me anything about how the platform works, or tap the mic to ask out loud.",
  ts: new Date().toISOString(),
};

const HomepageChat: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>(() => {
    const h = loadHistory();
    return h.length ? h : [GREETING];
  });
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [recording, setRecording] = useState(false);
  const [showEmailPrompt, setShowEmailPrompt] = useState(false);
  const [email, setEmail] = useState('');
  const [emailSaved, setEmailSaved] = useState<boolean>(() => !!localStorage.getItem(EMAIL_KEY));

  const sessionId = useRef<string>(getOrCreateSessionId());
  const scrollRef = useRef<HTMLDivElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);

  useEffect(() => {
    saveHistory(messages);
    requestAnimationFrame(() => {
      scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
    });
  }, [messages]);

  // ---------- Send text message ----------
  async function sendMessage(text: string, attachedEmail?: string) {
    const trimmed = text.trim();
    if (!trimmed || busy) return;

    const userMsg: ChatMessage = {
      role: 'user',
      content: trimmed,
      ts: new Date().toISOString(),
    };
    setMessages((m) => [...m, userMsg]);
    setInput('');
    setBusy(true);

    try {
      const res = await fetch(`${API_BASE}/public/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: sessionId.current,
          message: trimmed,
          ...(attachedEmail ? { visitor_email: attachedEmail } : {}),
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data: ChatResponse = await res.json();

      setMessages((m) => [
        ...m,
        { role: 'assistant', content: data.reply, ts: new Date().toISOString() },
      ]);
      if (data.suggest_email_capture && !emailSaved) {
        setShowEmailPrompt(true);
      }
    } catch (err) {
      setMessages((m) => [
        ...m,
        {
          role: 'assistant',
          content:
            "Sorry — I couldn't reach the server. Mind trying again in a moment?",
          ts: new Date().toISOString(),
        },
      ]);
    } finally {
      setBusy(false);
    }
  }

  // ---------- Voice recording ----------
  async function startRecording() {
    if (recording) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : 'audio/webm';
      const recorder = new MediaRecorder(stream, { mimeType });
      audioChunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) audioChunksRef.current.push(e.data);
      };
      recorder.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(audioChunksRef.current, { type: mimeType });
        if (blob.size === 0) return;
        await uploadAudio(blob);
      };
      recorder.start();
      mediaRecorderRef.current = recorder;
      setRecording(true);
    } catch (err) {
      setMessages((m) => [
        ...m,
        {
          role: 'assistant',
          content:
            "I couldn't access your microphone. Type your question instead — or check browser permissions.",
          ts: new Date().toISOString(),
        },
      ]);
    }
  }

  function stopRecording() {
    const r = mediaRecorderRef.current;
    if (r && r.state !== 'inactive') r.stop();
    mediaRecorderRef.current = null;
    setRecording(false);
  }

  async function uploadAudio(blob: Blob) {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('audio', blob, 'voice.webm');
      fd.append('session_id', sessionId.current);
      const res = await fetch(`${API_BASE}/public/chat/transcribe`, {
        method: 'POST',
        body: fd,
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const transcript: string = (data?.transcript || '').trim();
      if (!transcript) {
        setMessages((m) => [
          ...m,
          {
            role: 'assistant',
            content:
              "I didn't catch that — could you try again or type your question?",
            ts: new Date().toISOString(),
          },
        ]);
        return;
      }
      // Send the transcript as if the user typed it
      await sendMessage(transcript);
    } catch (err) {
      setMessages((m) => [
        ...m,
        {
          role: 'assistant',
          content:
            "Voice transcription is unavailable right now — please type your question.",
          ts: new Date().toISOString(),
        },
      ]);
    } finally {
      setBusy(false);
    }
  }

  // ---------- Email capture ----------
  function submitEmail() {
    const e = email.trim();
    if (!e || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e)) return;
    localStorage.setItem(EMAIL_KEY, e);
    setEmailSaved(true);
    setShowEmailPrompt(false);
    setMessages((m) => [
      ...m,
      {
        role: 'assistant',
        content:
          "Got it — Aaron will reach out. In the meantime, what else can I tell you?",
        ts: new Date().toISOString(),
      },
    ]);
    // Fire one more chat call with the email attached so server records it
    void sendMessage('(email captured)', e);
  }

  // ---------- Render ----------
  return (
    <>
      {/* Floating launcher */}
      <AnimatePresence>
        {!open && (
          <motion.button
            key="launcher"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            onClick={() => setOpen(true)}
            className="fixed bottom-6 right-6 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-apple-blue text-white shadow-xl transition hover:bg-apple-blueHover"
            aria-label="Open chat"
          >
            <MessageCircle className="h-6 w-6" />
          </motion.button>
        )}
      </AnimatePresence>

      {/* Chat panel */}
      <AnimatePresence>
        {open && (
          <motion.div
            key="panel"
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-6 right-6 z-50 flex h-[34rem] max-h-[calc(100vh-3rem)] w-[22rem] max-w-[calc(100vw-3rem)] flex-col overflow-hidden rounded-3xl bg-white shadow-2xl ring-1 ring-black/5"
          >
            {/* Header */}
            <div className="flex items-center justify-between border-b border-apple-divider px-5 py-3">
              <div>
                <p className="text-sm font-semibold text-apple-ink">Ask anything</p>
                <p className="text-xs text-apple-subtext">VentureOS</p>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="rounded-full p-1.5 text-apple-subtext transition hover:bg-apple-bg hover:text-apple-ink"
                aria-label="Close chat"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Messages */}
            <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto px-4 py-4">
              {messages.map((m, i) => (
                <div
                  key={i}
                  className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[85%] whitespace-pre-wrap break-words rounded-2xl px-3.5 py-2 text-sm leading-relaxed ${
                      m.role === 'user'
                        ? 'bg-apple-blue text-white'
                        : 'bg-apple-bg text-apple-ink'
                    }`}
                  >
                    {m.content}
                  </div>
                </div>
              ))}
              {busy && (
                <div className="flex justify-start">
                  <div className="rounded-2xl bg-apple-bg px-3.5 py-2 text-sm text-apple-subtext">
                    <Loader2 className="inline h-4 w-4 animate-spin" />
                  </div>
                </div>
              )}
            </div>

            {/* Email capture banner */}
            {showEmailPrompt && (
              <div className="border-t border-apple-divider bg-apple-bg/60 px-4 py-3">
                <p className="text-xs text-apple-subtext">
                  Want Aaron to follow up? Drop your email:
                </p>
                <div className="mt-2 flex items-center gap-2">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@company.com"
                    className="flex-1 rounded-full border border-apple-divider bg-white px-3 py-1.5 text-sm placeholder:text-apple-subtext/60 focus:border-apple-blue focus:outline-none"
                  />
                  <button
                    onClick={submitEmail}
                    className="rounded-full bg-apple-blue px-3 py-1.5 text-xs font-medium text-white hover:bg-apple-blueHover"
                  >
                    Save
                  </button>
                  <button
                    onClick={() => setShowEmailPrompt(false)}
                    className="rounded-full px-2 py-1.5 text-xs text-apple-subtext hover:text-apple-ink"
                  >
                    Skip
                  </button>
                </div>
              </div>
            )}

            {/* Composer */}
            <div className="flex items-center gap-2 border-t border-apple-divider px-3 py-3">
              <button
                onClick={recording ? stopRecording : startRecording}
                disabled={busy}
                className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full transition disabled:opacity-50 ${
                  recording
                    ? 'bg-red-500 text-white animate-pulse'
                    : 'bg-apple-bg text-apple-ink hover:bg-apple-divider'
                }`}
                aria-label={recording ? 'Stop recording' : 'Start voice recording'}
              >
                {recording ? <Square className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
              </button>
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    void sendMessage(input);
                  }
                }}
                disabled={busy || recording}
                placeholder={recording ? 'Listening…' : 'Ask a question…'}
                className="flex-1 rounded-full border border-apple-divider bg-white px-4 py-2 text-sm placeholder:text-apple-subtext/60 focus:border-apple-blue focus:outline-none disabled:bg-apple-bg/40"
              />
              <button
                onClick={() => void sendMessage(input)}
                disabled={busy || recording || !input.trim()}
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-apple-blue text-white transition hover:bg-apple-blueHover disabled:opacity-50"
                aria-label="Send message"
              >
                <Send className="h-4 w-4" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default HomepageChat;
